<template>
	<div>
		<div v-if="hasEditedItems && !hasUneditedItems" class="item-edit pl-0">
			<p>Folgende Erfahrungen hast du bereits eingetragen:</p>
			<SelectedItems
				:characteristic-type="characteristicType"
				:characteristic-items="characteristicItems"
				:selected="editedItems"
				deletable
				with-edit-button
				:label-type="labelType"
				flat
				@deleteItem="itemToDelete = $event"
				@editItem="editItem($event)"
			/>
		</div>
		<template v-if="hasUneditedItems">
			<slot name="questionFurtherItems"/>
			<template v-if="additionConfig.inline">
				<h3 v-if="subTitle" class="my-4">{{ subTitle }}</h3>
				<div
					v-for="item in relevantItems"
					:key="item.item.id"
					class="mb-3"
				>
					<h2>
						<Label :characteristic="item.item" :label-type="labelType"/>
						<template v-if="getSupplements(item)">({{ getSupplements(item) }})</template>
					</h2>
					<AdditionEdit
						:item="item"
						:experience-config="additionConfig.experience"
						:self-assessment-config="additionConfig.selfAssessment"
						:application-desire="isDesire(item)"
						class="pl-5"
						:type="type"
						@change="onAdditionChange(item, $event)"
					/>
				</div>
			</template>
			<template v-else>
				<SelectedItems
					:characteristic-type="characteristicType"
					:characteristic-items="characteristicItems"
					:selected="uneditedItems"
					:label-type="labelType"
					deletable
					flat
					@deleteItem="itemToDelete = $event"
				/>
				<div class="text-right">
					<Button
						:text="buttonText"
						secondary
						smaller
						@click="openAdditionsMatrixFan"
					/>
				</div>
			</template>
		</template>
		<div v-else class="mt-4">
			<div class="messages correct-inner-left-padding">
				<div
					v-for="(message, j) in computedMessages"
					:key="'forced-message--' + j"
					:style="{'animation-delay': (j + 's')}"
					class="message"
				>
					<span v-html="message"/>
				</div>
			</div>
			<div class="item-edit" :style="{'animation-delay': (computedMessages.length + 's')}">
				<RadioGroup
					v-model="further"
					:options="furtherOptions"
					stacked
					@change="furtherChanged"
				/>
			</div>
		</div>
		<ConfirmPopUp
			v-if="itemToDelete"
			:title="deleteConfirmTitle"
			@confirm="deleteItem"
			@hidden="itemToDelete = undefined"
		>
			<slot name="deleteConfirmText" :item="itemToDelete"/>
		</ConfirmPopUp>
	</div>
</template>

<script>
import SelectedItems from "@/vue/characteristic/SelectedItems.vue";
import Button from "@/vue/common/Button.vue";
import {mapActions} from "vuex";
import _ from "lodash";
import RadioGroup from "@/vue/common/form/RadioGroup.vue";
import ConfirmPopUp from "@/vue/common/popup/ConfirmPopUp.vue";
import Label from "@/vue/characteristic/Label.vue";
import AdditionEdit from "@/vue/characteristic/addition/Edit.vue";
import {isDefined} from "@/helper/Value";
import InfoBox from "@/vue/common/InfoBox.vue";
import {APPLICATION} from "@/helper/characteristic/Label";
import {byId} from "@/helper/application/characteristic/Provider";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";
import {getAsList} from "@/helper/characteristic/Supplement";

const FURTHER_YES_NOW = 'yes-now';
const FURTHER_YES_LATER = 'yes-later';
const FURTHER_NO = 'no';

const TYPE__APPLICATION = 'application';
const TYPE__JOB_PROFILE = 'job-profile';

const FURTHER_LABELS__APPLICATION_EMPTY = {
	[FURTHER_YES_NOW]: 'Eingeben und Chancen erhöhen',
	[FURTHER_YES_LATER]: 'Später eingeben',
	[FURTHER_NO]: 'Ich habe noch keine Erfahrungen',
};

const FURTHER_LABELS__APPLICATION = {
	[FURTHER_YES_NOW]: 'Weitere eingeben und meine Chancen erhöhen',
	[FURTHER_YES_LATER]: 'Später eingeben',
	[FURTHER_NO]: 'Ich habe meine Erfahrungen vollständig erfasst, weiter geht\'s',
};

const FURTHER_LABELS__JOB_PROFILE = {
	[FURTHER_YES_NOW]: 'Ja',
	[FURTHER_YES_LATER]: 'Später eingeben',
	[FURTHER_NO]: 'Nein',
};

export default {
	components: {InfoBox, AdditionEdit, Label, ConfirmPopUp, RadioGroup, Button, SelectedItems},

	props: {
		type: {
			type: String,
			required: false,
			default: TYPE__APPLICATION,
		},
		subTitle: {
			type: String,
			required: false,
		},
		labelType: {
			type: String,
			required: false,
			default: APPLICATION
		},
		characteristicType: {
			type: Object,
			required: true,
		},
		characteristicItems: {
			type: Array,
			required: true,
		},
		desire: {
			type: Array,
			required: false,
		},
		additionConfig: {
			type: Object,
			required: false,
		},
		supplementsConfig: {
			type: Object,
			required: false,
		},
		buttonText: {
			type: String,
			required: false,
			default: 'Erfahrung / Ausbildung eingeben'
		},
		deleteConfirmTitle: {
			type: String,
			required: false,
			default: 'Eintrag entfernen'
		},
		furtherMessages: {
			type: Array,
			required: false,
			default: () => []
		},
		furtherMessagesEmpty: {
			type: Array,
			required: false,
			default: () => []
		},
		groupByLetter: {
			type: Object,
			required: false,
		}
	},

	mounted ()
	{
		if (!this.hasItems)
		{
			this.$emit('furtherAppeared', {
				hasData: false
			});
		}
	},

	data ()
	{
		let items = [];

		if (this.desire)
		{
			_.forEach(this.desire, item =>
			{
				const characteristic = byId(item.item.id, this.characteristicItems);

				if (characteristic.children.length === 0)
				{
					items.push({
						...item,
						item: characteristic,
					});
				}
			});
		}

		return {
			items,
			further: undefined,
			itemToDelete: undefined,
		};
	},

	computed: {
		computedMessages ()
		{
			if (!this.hasItems)
			{
				return this.furtherMessagesEmpty || this.furtherMessages;
			}

			return this.furtherMessages;
		},
		hasItems ()
		{
			return this.items && this.items.length > 0;
		},
		furtherOptions ()
		{
			const furtherLabels = this.type === TYPE__APPLICATION
				? (this.hasItems ? FURTHER_LABELS__APPLICATION : FURTHER_LABELS__APPLICATION_EMPTY)
				: FURTHER_LABELS__JOB_PROFILE;

			return this.type === TYPE__APPLICATION ? [
				{value: FURTHER_YES_NOW, text: furtherLabels[FURTHER_YES_NOW]},
				{value: FURTHER_YES_LATER, text: furtherLabels[FURTHER_YES_LATER]},
				{value: FURTHER_NO, text: furtherLabels[FURTHER_NO]}
			] : [
				{value: FURTHER_YES_NOW, text: furtherLabels[FURTHER_YES_NOW]},
				{value: FURTHER_NO, text: furtherLabels[FURTHER_NO]},
				{value: FURTHER_YES_LATER, text: furtherLabels[FURTHER_YES_LATER]}
			];
		},
		relevantItems ()
		{
			return _.filter(this.items, item => item.item.children.length === 0);
		},
		uneditedItems ()
		{
			return _.filter(this.relevantItems, item => !this.additionsFinished(item));
		},
		editedItems ()
		{
			return _.filter(this.relevantItems, item => item.hasAdditions);
		},
		hasUneditedItems ()
		{
			return this.uneditedItems.length > 0;
		},
		hasEditedItems ()
		{
			return this.editedItems && this.editedItems.length > 0;
		},
		valid ()
		{
			return isDefined(this.further);
		}
	},

	methods: {
		...mapActions('characteristic/addition/matrix/fan', {
			showAdditionMatrixFan: 'show',
		}),
		...mapActions('characteristic/fan', {
			showCharacteristicFan: 'show',
		}),
		...mapActions('characteristic/addition/fan', {
			showAdditionFan: 'show'
		}),
		getSupplements (item)
		{
			if (!item.supplements)
			{
				return undefined;
			}

			return getAsList(item.supplements);
		},
		isDesire (item)
		{
			if (!this.desire)
			{
				return false;
			}

			const ids = _.map(this.desire, item => item.item.id);

			return _.includes(ids, item.item.id);
		},
		additionsFinished (item)
		{
			if (this.additionConfig?.experience?.enabled && item.experience === undefined)
			{
				return false;
			}

			if (this.additionConfig?.selfAssessment?.enabled && item.selfAssessment === undefined)
			{
				return false;
			}

			return true;
		},
		onAdditionChange (item, data)
		{
			this.$set(item, 'experience', data.experience);
			this.$set(item, 'selfAssessment', data.selfAssessment);
			this.$set(item, 'educationsFinished', data.educationsFinished);
			this.$set(item, 'educationsUnfinished', data.educationsUnfinished);
			this.$set(item, 'hasAdditions', true);

			this.$nextTick(() =>
			{
				if (!this.hasUneditedItems)
				{
					this.$emit('furtherAppeared', {
						hasData: true
					});
				}
			});

			this.emitChangeEvent();
		},
		editItem (item)
		{
			this.showAdditionFan({
				characteristic: item.item,
				item,
				type: this.type,
				experienceConfig: this.additionConfig?.experience,
				selfAssessmentConfig: this.additionConfig?.selfAssessment,
				educationConfig: this.additionConfig?.education,
				onClose: data =>
				{
					if (!data.confirmed)
					{
						return;
					}

					this.$set(item, 'experience', data.experience);
					this.$set(item, 'selfAssessment', data.selfAssessment);
					this.$set(item, 'educations', data.educations);
					this.$set(item, 'hasAdditions', true);

					this.emitChangeEvent();
				}
			});
		},
		deleteItem ()
		{
			this.items = _.filter(this.items, i => i.item.id !== this.itemToDelete.item.id);

			this.itemToDelete = undefined;

			this.emitChangeEvent();
		},
		furtherChanged (value)
		{
			let action = _.find(this.furtherOptions, option => option.value === this.further)?.text;

			if (action && this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
			);

			if (value === FURTHER_YES_NOW)
			{
				this.openCharacteristicEditFan();

				this.further = undefined;
			}

			this.emitChangeEvent();
		},
		openAdditionsMatrixFan ()
		{
			this.showAdditionMatrixFan({
				type: this.type,
				title: this.additionConfig?.title,
				subTitle: this.additionConfig?.subTitle,
				items: _.cloneDeep(this.uneditedItems),
				experienceConfig: this.additionConfig?.experience,
				selfAssessmentConfig: this.additionConfig?.selfAssessment,
				educationConfig: this.additionConfig?.education,
				onClose: data =>
				{
					if (!data.confirmed)
					{
						return;
					}

					_.forEach(data.items, editedItem =>
					{
						const index = _.findIndex(
							this.items,
							relevantItem => relevantItem.item.id === editedItem.item.id
						);

						editedItem.hasAdditions = true;

						this.$set(this.items, index, editedItem);
					});

					this.$emit('additionsEdit');

					this.emitChangeEvent();
				}
			});
		},
		openCharacteristicEditFan ()
		{
			this.showCharacteristicFan({
				selected: this.items,
				characteristicType: this.characteristicType,
				characteristicItems: this.characteristicItems,
				supplementsConfig: this.supplementsConfig ?? {
					disable: true
				},
				withKeyword: true,
				groupByLetter: this.groupByLetter,
				onChange: ({selected}) =>
				{
					this.items = selected;

					this.$nextTick(() =>
					{
						if (this.hasUneditedItems)
						{
							this.openAdditionsMatrixFan();
						}
					});
				},
			});
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', {
				items: this.items,
				skipped: this.further === FURTHER_YES_LATER
			});
		}
	}
}
</script>
